import { createBaseApi } from '../baseApi';
import { uploadFiles } from '../../../utils/uploadUtils';

const profileApi = createBaseApi('profileApi');

export const enhancedProfileApi = profileApi.injectEndpoints({
  endpoints: (builder) => ({
    getProfile: builder.query({
      query: () => '/profile/me',
      providesTags: ['User', 'Profile'],
      transformResponse: (response) => {
        if (response) {
          return {
            ...response,
            profilePic: response.profilePic || (response.profilePicUrl ? { url: response.profilePicUrl } : null)
          };
        }
        return response;
      }
    }),
    getUserProfile: builder.query({
      query: (userId) => `/users/${userId}?populate=profilePic`,
      transformResponse: (response) => {
        // Return a sanitized user profile
        const { id, username, email, firstname, lastname, profilePic, profilePicUrl, blocked, confirmed } = response;
        return {
          id,
          username,
          email,
          firstname,
          lastname,
          profilePic: profilePic || (profilePicUrl ? { url: profilePicUrl } : null),
          profilePicUrl,
          blocked,
          confirmed
        };
      },
      providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
    }),
    /**
     * Get limited customer profile data for reviews
     * Uses the new secure endpoint that only returns public information
     */
    getCustomerProfileForReview: builder.query({
      query: (customerId) => `/profile/customer/${customerId}`,
      providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
    }),
    /**
     * Get user profile with proper profilePic population
     * Uses the new endpoint specifically designed to fix profile picture retrieval
     */
    getUserProfileById: builder.query({
      query: (userId) => `/profile/user/${userId}`,
      transformResponse: (response) => ({
        ...response,
        profilePic: response.profilePic || (response.profilePicUrl ? { url: response.profilePicUrl } : null),
      }),
      providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
    }),
    updateProfile: builder.mutation({
      query: (profileData) => ({
        url: '/profile/me',
        method: 'PUT',
        body: profileData
      }),
      invalidatesTags: ['User', 'Profile']
    }),
    /**
     * Change password using our custom secure endpoint
     * This uses a dedicated endpoint that properly verifies the current password
     * before updating to the new password
     */
    changePassword: builder.mutation({
      query: (passwordData) => ({
        url: '/password-change',
        method: 'POST',
        body: {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        }
      }),
      invalidatesTags: ['User', 'Profile']
    }),
    /**
     * Follow or unfollow a user
     */
    followUser: builder.mutation({
      query: (userId) => ({
        url: `/profile/follow/${userId}`,
        method: 'POST'
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'User', id: arg },
        'Profile'
      ]
    }),
    /**
     * Get merchant profile by ID
     */
    getMerchantProfile: builder.query({
      query: (merchantId) => `/users/${merchantId}?populate=profilePic,backgroundPic,addresses,followers,following,commentsFromCustomers`,
      transformResponse: (response) => {
        // Transform response if needed
        const { id, username, profilePic, profilePicUrl, backgroundPic, addresses, bio, followers, following, commentsFromCustomers } = response;

        // Calculate average rating
        const averageRating = commentsFromCustomers?.length
          ? commentsFromCustomers.reduce((sum, comment) => sum + (comment.rating || 0), 0) / commentsFromCustomers.length
          : 0;

        return {
          id,
          username,
          profilePic: profilePic || (profilePicUrl ? { url: profilePicUrl } : null),
          profilePicUrl,
          backgroundPic,
          addresses,
          bio,
          followersCount: followers?.length || 0,
          followingCount: following?.length || 0,
          commentsFromCustomers,
          averageRating,
          totalRatings: commentsFromCustomers?.length || 0
        };
      },
      providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
    }),
    /**
     * Get complete merchant profile with following status and product counts
     * Uses the dedicated merchant endpoint
     */
    getMerchantFullProfile: builder.query({
      query: (merchantId) => `/merchant/${merchantId}/profile`,
      providesTags: (result, error, arg) => [
        { type: 'User', id: arg },
        'Profile',
        'Merchant'
      ]
    }),
    /**
     * Update the user's avatar (profile picture)
     * Handles file upload and profile update in one operation
     */
    updateAvatar: builder.mutation({
      queryFn: async (fileData, { dispatch }, _extraOptions, baseQuery) => {
        try {
          if (!fileData) {
            return { error: { message: 'No file provided' } };
          }

          // Step 1: Upload the file
          const uploadedFiles = await uploadFiles([fileData]);

          if (!uploadedFiles || uploadedFiles.length === 0) {
            return { error: { message: 'File upload failed' } };
          }


          // Step 2: Update the profile with the file ID
          try {
            const result = await baseQuery({
              url: '/profile/me',
              method: 'PUT',
              body: { profilePic: uploadedFiles[0].id }
            });

            if (result.error) {
              console.error('Profile update error:', result.error);
              return { error: result.error };
            }

            // Invalidate the profile cache to refresh data
            dispatch(profileApi.util.invalidateTags(['User', 'Profile']));

            return { data: result.data };
          } catch (apiError) {
            console.error('API request error:', apiError);
            return { error: { message: apiError.message || 'API request failed' } };
          }
        } catch (error) {
          console.error('Avatar update error:', error);
          return { error: { message: error.message || 'Failed to update avatar' } };
        }
      },
      invalidatesTags: ['User', 'Profile']
    }),
    /**
     * Update the user's background image
     * Handles file upload and profile update in one operation
     */
    updateBackground: builder.mutation({
      queryFn: async (fileData, { dispatch }, _extraOptions, baseQuery) => {
        try {
          if (!fileData) {
            return { error: { message: 'No file provided' } };
          }

          // Step 1: Upload the file
          const uploadedFiles = await uploadFiles([fileData]);

          if (!uploadedFiles || uploadedFiles.length === 0) {
            return { error: { message: 'File upload failed' } };
          }


          // Step 2: Update the profile with the file ID
          try {
            const result = await baseQuery({
              url: '/profile/me',
              method: 'PUT',
              body: { backgroundPic: uploadedFiles[0].id }
            });

            if (result.error) {
              console.error('Profile update error:', result.error);
              return { error: result.error };
            }

            // Invalidate the profile cache to refresh data
            dispatch(profileApi.util.invalidateTags(['User', 'Profile']));

            return { data: result.data };
          } catch (apiError) {
            console.error('API request error:', apiError);
            return { error: { message: apiError.message || 'API request failed' } };
          }
        } catch (error) {
          console.error('Background update error:', error);
          return { error: { message: error.message || 'Failed to update background image' } };
        }
      },
      invalidatesTags: ['User', 'Profile']
    }),
    /**
     * Delete the user's avatar (profile picture)
     */
    deleteAvatar: builder.mutation({
      query: () => ({
        url: '/profile/me',
        method: 'PUT',
        body: { profilePic: null }
      }),
      invalidatesTags: ['User', 'Profile']
    }),
    /**
     * Delete the user's background image
     */
    deleteBackground: builder.mutation({
      query: () => ({
        url: '/profile/me',
        method: 'PUT',
        body: { backgroundPic: null }
      }),
      invalidatesTags: ['User', 'Profile']
    }),
    /**
     * Get followers list for a user
     */
    getFollowers: builder.query({
      query: (userId) => `/merchant/${userId}/followers`,
      providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
    }),
    /**
     * Get following list for a user
     */
    getFollowing: builder.query({
      query: (userId) => `/merchant/${userId}/following`,
      providesTags: (result, error, arg) => [{ type: 'User', id: arg }]
    }),
    /**
     * Delete the user's account
     */
    deleteAccount: builder.mutation({
      query: () => ({
        url: '/profile/me',
        method: 'DELETE'
      }),
      invalidatesTags: ['User', 'Profile']
    })
  })
});

export const {
  useGetProfileQuery,
  useGetUserProfileQuery,
  useGetUserProfileByIdQuery,
  useGetCustomerProfileForReviewQuery,
  useUpdateProfileMutation,
  useChangePasswordMutation,
  useUpdateAvatarMutation,
  useUpdateBackgroundMutation,
  useDeleteAvatarMutation,
  useDeleteBackgroundMutation,
  useFollowUserMutation,
  useGetMerchantProfileQuery,
  useGetMerchantFullProfileQuery,
  useGetFollowersQuery,
  useGetFollowingQuery,
  useDeleteAccountMutation
} = enhancedProfileApi; 